/**
 * Стикеры незакрытых генераций — «где я это запустил».
 *
 * Жалоба владельца 2026-09-21: запустил генерацию в карточке-инструменте, ушёл
 * на другую страницу — прогресс пропал; вернулся — обычный виджет, и карточка
 * не показывает, что по ней шла работа. Причина: виджет отпускает пин на уходе
 * (GenerateWidget cleanup), а в снимке генерации не было ни карточки, ни пути —
 * возвращать было некуда.
 *
 * Стикер живёт отдельно от виджета и от пина: он привязан к САМОЙ генерации.
 * Появляется на старте, переживает переходы и завершение, и снимается только
 * двумя способами — человек кликнул по нему (ушёл к результату) или закрыл
 * крестиком. Автоскрытия нет намеренно: прежняя пилюля гасла через 5 секунд и
 * человек, отошедший от экрана, терял единственную ниточку к результату.
 *
 * sessionStorage, а не localStorage: стикер из вчерашней вкладки — мусор,
 * а внутри вкладки он обязан пережить и навигацию, и F5. Тот же выбор, что
 * у пина карточки (`attachedStyleCard`).
 */
import { useEffect, useState } from 'react'

import type { GenerationOrigin } from '../context/historyPersistence'
import type { InputsSnapshot } from '../pages/generate/logic/inputsSnapshot'
import { deleteSourceImages } from '../utils/sourceImageDb'

export type StickerStatus = 'running' | 'done' | 'error'

export interface GenerationSticker {
	/** generationId снимка — один стикер на одну генерацию. */
	id: string
	status: StickerStatus
	/** Куда вернуть по клику + чем подписать. */
	origin: GenerationOrigin
	/** Подпись, когда карточки не было (генерация из общего виджета). */
	modelName: string
	/** Модель генерации: по ней виджет понимает, что готовый результат — его. */
	modelId: string
	/** Картинка стикера: результат, когда готов; до этого — обложка карточки. */
	resultUrl?: string
	/** Полный набор ссылок результата — нужен при возврате в инструмент. */
	resultUrls?: string[] | null
	/** id запроса: по нему инструмент восстанавливает оценку 👍/👎. */
	requestId?: string | null
	/** Ключ загруженного юзером фото в IndexedDB — вернуть его в слот.
	 *  Одиночный путь: для стикеров без снимка входов (см. `inputs`). */
	sourceImageDbKey?: string
	/** Снимок ВСЕХ входов на момент старта — слоты, второе фото, видео, аудио
	 *  (см. inputsSnapshot.ts). С ним возврат ставит каждый канал на место. */
	inputs?: InputsSnapshot
	/** Текст ошибки для состояния error. */
	error?: string
	startedAt: number
}

const KEY = 'umnik.generation_stickers'
const CHANGED = 'umnik:generation-stickers-changed'
/** Потолок стопки: больше — это уже не подсказка, а стена. Режем самые старые. */
const MAX = 4

function read(): GenerationSticker[] {
	try {
		const raw = sessionStorage.getItem(KEY)
		if (!raw) return []
		const list = JSON.parse(raw)
		return Array.isArray(list) ? (list as GenerationSticker[]) : []
	} catch {
		return []
	}
}

/** Файлы снимка входов живут ровно столько, сколько стикер: снят — удалили.
 *  Первичный ключ истории (generationId) в `keys` не входит и остаётся. */
function dropFiles(list: GenerationSticker[]): void {
	const keys = list.flatMap((s) => s.inputs?.keys ?? [])
	if (keys.length) void deleteSourceImages(keys)
}

/** Кого вытеснить сверх потолка: сначала законченные (старые раньше), идущие — только если законченных не хватило.
 *  После «Продолжить в фоне» идущую генерацию ведёт именно стикер: выбросить его — потерять её из виду. */
function overflow(list: GenerationSticker[]): Set<string> {
	const excess = list.length - MAX
	const out = new Set<string>()
	if (excess <= 0) return out
	for (const s of [...list.filter((x) => x.status !== 'running'), ...list.filter((x) => x.status === 'running')]) {
		if (out.size >= excess) break
		out.add(s.id)
	}
	return out
}

function write(list: GenerationSticker[]): void {
	// Вытесненные из стопки — тоже снятые: их файлы никому больше не нужны.
	const evicted = overflow(list)
	dropFiles(list.filter((s) => evicted.has(s.id)))
	try {
		sessionStorage.setItem(KEY, JSON.stringify(list.filter((s) => !evicted.has(s.id))))
	} catch {
		/* quota — стикеры просто не переживут переход, генерация не страдает */
	}
	window.dispatchEvent(new CustomEvent(CHANGED))
}

/** Снять всю стопку — выход из аккаунта (utils/forgetPendingGenerations): чужие генерации следующему человеку не нужны. */
export function clearStickers(): void {
	const list = read()
	if (list.length === 0) return
	dropFiles(list)
	owned.clear()
	try {
		sessionStorage.removeItem(KEY)
	} catch {
		/* хранилище недоступно */
	}
	window.dispatchEvent(new CustomEvent(CHANGED))
}

export function listStickers(): GenerationSticker[] {
	return read()
}

// ── Кто ведёт генерацию стикера ──────────────────────────────
//
// Пока генерацию ведёт живой запуск виджета (он её начал и ждёт ответа сервера — и после «Продолжить в фоне»
// тоже), стикер обновляет он. Стикер без ведущего — вкладку перезагрузили — доводит опрос сервера в стопке
// (GenerationStickers): генерацию закрывает ответ сервера, а не часы (план «Ждём ответа нейросети», Ф2).
// Реестр — в памяти вкладки: перезагрузка обнуляет его, и все идущие стикеры переходят к стопке.
const owned = new Set<string>()

export function claimSticker(id: string): void {
	owned.add(id)
}

export function releaseSticker(id: string): void {
	owned.delete(id)
}

export function isStickerOwned(id: string): boolean {
	return owned.has(id)
}

/** Поставить стикер на старте генерации. */
export function addSticker(sticker: GenerationSticker): void {
	write([...read().filter((s) => s.id !== sticker.id), sticker])
}

/**
 * Обновить существующий стикер. Если его уже сняли крестиком — НЕ воскрешаем:
 * завершение генерации не должно возвращать то, что человек убрал руками.
 */
export function updateSticker(id: string, patch: Partial<GenerationSticker>): void {
	const list = read()
	const i = list.findIndex((s) => s.id === id)
	if (i === -1) return
	const next = [...list]
	next[i] = { ...next[i], ...patch }
	write(next)
}

export function removeSticker(id: string): void {
	const list = read()
	dropFiles(list.filter((s) => s.id === id))
	write(list.filter((s) => s.id !== id))
}

/**
 * Незакрытая работа по этой карточке — для прихода НЕ по стикеру.
 *
 * Заявку оставляет клик по стикеру, но человек может вернуться и обычным путём:
 * через меню и плитку. Тогда заявки нет, а работа всё ещё не закрыта — и слот с
 * зоной результата открывались пустыми. Стикер тут и есть признак незакрытого:
 * он держится до клика или крестика, поэтому по нему же и восстанавливаем.
 *
 * Берём последний подходящий: если по карточке успели запустить несколько,
 * человек ждёт свежий.
 */
export function findStickerForCard(cardId?: string | null): GenerationSticker | null {
	if (!cardId) return null
	const mine = read().filter((s) => sameCard(s.origin.cardId, cardId))
	return mine.length ? mine[mine.length - 1] : null
}

export function useStickers(): GenerationSticker[] {
	const [list, setList] = useState<GenerationSticker[]>(read)
	useEffect(() => {
		const reload = () => setList(read())
		window.addEventListener(CHANGED, reload)
		// Догоняем emit, случившийся между рендером и подпиской — тот же
		// случай, что у useAttachedStyleCard (cleanup уходящего дерева
		// стреляет раньше mount-эффектов нового).
		reload()
		return () => window.removeEventListener(CHANGED, reload)
	}, [])
	return list
}


// ── Заявка на возврат результата в инструмент ────────────────
//
// Клик по готовому стикеру возвращает на страницу карточки, а там карточка
// применяется заново — и `applyCardToGenerate` штатно чистит зону результата
// (так лечили «Воссоздать», который оставлял в ней прошлый кадр, жалоба
// 2026-07-25). Свежий результат стирался вместе со старым.
//
// Поэтому возврат оставляет адресную заявку: «для этой карточки покажи вот
// этот результат». `applyCard` её забирает и вместо очистки восстанавливает.
// Заявка одноразовая и живёт минуту: если человек по дороге ушёл в другое
// место, она протухнет и ничего не подменит.

const RESTORE_KEY = 'umnik.sticker_restore'
const RESTORE_TTL_MS = 60_000

export interface StickerRestore {
	/** Карточка, к которой относится результат (undefined — генерация без карточки). */
	cardId?: string
	/** Пусто у ИДУЩЕЙ генерации: результата ещё нет, возвращаем только фото. */
	resultUrl?: string
	resultUrls?: string[] | null
	requestId?: string | null
	/** Фото юзера: слот должен вернуться и у идущей генерации. */
	sourceImageDbKey?: string
	/** Снимок всех входов — приоритетнее одиночного ключа. */
	inputs?: InputsSnapshot
	at: number
}

/** Время проставляет стор: в теле React-компонента `Date.now()` — нечистый вызов. */
export function requestRestore(req: Omit<StickerRestore, 'at'>): void {
	try {
		sessionStorage.setItem(RESTORE_KEY, JSON.stringify({ ...req, at: Date.now() }))
	} catch {
		/* quota — результат просто не восстановится, поведение как раньше */
	}
}

/**
 * Одна карточка приезжает под разными id: в пине лежит `card_1784…`
 * (AttachedStyleCard.id), а виджету её отдают как `popular-card_1784…`
 * (`applyPopularItem` строит GenerationCard с префиксом). Сводим явно, чтобы
 * заявка находила свою карточку независимо от того, чей id пришёл.
 */
function sameCard(a?: string | null, b?: string | null): boolean {
	const norm = (v?: string | null) => (v ? v.replace(/^popular-/, '') : null)
	return norm(a) === norm(b)
}

/**
 * Забрать заявку, если она про ЭТУ карточку. Одноразово: прочитали — стёрли,
 * иначе следующее применение той же карточки («Воссоздать») подсунуло бы
 * старый результат поверх новой генерации.
 */
export function takeRestore(cardId?: string | null): StickerRestore | null {
	try {
		const raw = sessionStorage.getItem(RESTORE_KEY)
		if (!raw) return null
		const req = JSON.parse(raw) as StickerRestore
		if (!req) return null
		if (Date.now() - req.at > RESTORE_TTL_MS) {
			sessionStorage.removeItem(RESTORE_KEY)
			return null
		}
		if (!sameCard(req.cardId, cardId)) return null
		sessionStorage.removeItem(RESTORE_KEY)
		return req
	} catch {
		return null
	}
}
